import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { CartItem } from "../../interfaces/cartInterface";
import { fetchFullCart } from "../../utils/api/apiGuest";

interface FullCartProduct extends CartItem {
  name: string;
  price: number;
  tax: number;
  total: number;
  image?: string;
  unit?: string;
  isAvailable: boolean;
  pickUpFrom?: string | null;
  pickUpUntill?: string | null;
}

interface TaxSummary {
  rate: number;
  amount: number;
}

interface FullCartState {
  cartId: string | null;
  products: FullCartProduct[];
  total: number;
  taxes: TaxSummary[];
  pickUpFrom?: string | null;
  pickUpUntill?: string | null;
  requiredPreparationDays: number;
  loading: boolean;
  error: string | null;
}

const initialState: FullCartState = {
  cartId: null,
  products: [],
  total: 0,
  taxes: [],
  requiredPreparationDays: 0,
  loading: false,
  error: null,
};

export const loadFullCart = createAsyncThunk(
  "fullCart/load",
  async (cartId: string) => {
    const res = await fetchFullCart(cartId);
    return res.data?.data;
  }
);

/**
 * Redux slice for managing the full shopping cart state.
 *
 * This slice handles the cart's loading state, error handling, and updates to the cart's
 * contents, including products, totals, tax summary, pickup information, and preparation days.
 * It provides a reducer to clear the cart and extra reducers to handle asynchronous loading
 * of the cart via `loadFullCart`.
 *
 * @remarks
 * - The `clearFullCart` reducer resets the state to its initial values.
 * - The `extraReducers` handle the pending, fulfilled, and rejected states of the `loadFullCart` async thunk.
 *
 * @example
 * // Dispatch to clear the cart
 * dispatch(clearFullCart());
 *
 * // Dispatch to load the cart asynchronously
 * dispatch(loadFullCart());
 */
const fullCartSlice = createSlice({
  name: "fullCart",
  initialState,
  reducers: {
    clearFullCart: (state) => {
      Object.assign(state, initialState);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadFullCart.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadFullCart.fulfilled, (state, action) => {
        const cart = action.payload;
        state.cartId = cart.cartId;
        state.products = cart.items;
        state.total = cart.total;
        state.taxes = cart.taxes;
        state.pickUpFrom = cart.pickUpFrom || null;
        state.pickUpUntill = cart.pickUpUntill || null;
        state.requiredPreparationDays = cart.requiredPreparationDays || 0;
        state.loading = false;
      })
      .addCase(loadFullCart.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "Fehler beim Laden des Warenkorbs";
      });
  },
});

export const { clearFullCart } = fullCartSlice.actions;
export default fullCartSlice.reducer;
